// Pure helpers of the Files tab: folder permissions, name check, the ACL draft and plain-language
// error messages.
import type {
  FolderAclDtoOutput,
  FolderDetailDtoOutput,
  FolderDtoOutput,
  PutFolderAclDto,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { FolderNameSchema } from '@batiflow/contracts';
import type { TFunction } from 'i18next';

export type Folder = FolderDtoOutput;
export type FolderDetail = FolderDetailDtoOutput;
export type Translate = TFunction<'documents'>;

/** Pickers load one page of at most 200 (the API's maximum). */
export const PICKER_SIZE = 200;

/** `If-Match` value for a version (`W/"<version>"`). */
export const etagOf = (version: number): string => `W/"${String(version)}"`;

// --- What the caller may do (the folder's `_permissions`).

interface WithPermissions {
  _permissions: readonly string[];
}

export const canManage = (folder: WithPermissions): boolean =>
  folder._permissions.includes('can_manage');
/** Managing a folder implies writing in it. */
export const canWrite = (folder: WithPermissions): boolean =>
  folder._permissions.includes('can_write') || canManage(folder);

// --- Folder name.

/** The trimmed name when valid, else the key of the message (`name.*`) to show. */
export function checkFolderName(
  input: string,
): { ok: true; name: string } | { ok: false; message: 'required' | 'tooLong' | 'invalid' } {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, message: 'required' };
  if (trimmed.length > 200) return { ok: false, message: 'tooLong' };
  const parsed = FolderNameSchema.safeParse(trimmed);
  return parsed.success ? { ok: true, name: parsed.data } : { ok: false, message: 'invalid' };
}

// --- Custom ACL draft.

export type AclRelation = 'reader' | 'writer';
export type AclSubjectType = 'user' | 'organization_members';

export interface AclDraftEntry {
  relation: AclRelation;
  subjectType: AclSubjectType;
  subjectId: string;
  displayName: string;
}

export interface AclDraft {
  mode: 'inherit' | 'custom';
  entries: AclDraftEntry[];
}

export const draftOf = (acl: FolderAclDtoOutput): AclDraft => ({
  mode: acl.mode,
  entries: acl.entries.map(({ relation, subjectType, subjectId, displayName }) => ({
    relation,
    subjectType,
    subjectId,
    displayName,
  })),
});

const entryKey = (entry: AclDraftEntry): string =>
  `${entry.subjectType}:${entry.subjectId}:${entry.relation}`;

/** Same mode and, in custom mode, the same entries (the order does not matter). */
export function sameAcl(a: AclDraft, b: AclDraft): boolean {
  if (a.mode !== b.mode) return false;
  if (a.mode === 'inherit') return true;
  const keys = new Set(a.entries.map(entryKey));
  return a.entries.length === b.entries.length && b.entries.every((e) => keys.has(entryKey(e)));
}

/** The PUT body: an inherited ACL carries no entry (the API refuses them). */
export function aclBody(draft: AclDraft): PutFolderAclDto {
  return {
    mode: draft.mode,
    entries:
      draft.mode === 'inherit'
        ? []
        : draft.entries.map(({ relation, subjectType, subjectId }) => ({
            relation,
            subjectType,
            subjectId,
          })),
  };
}

// --- Server answers.

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

/** 403 STEP_UP_REQUIRED: the app opens its step-up dialog and replays the action itself. */
export const isStepUpRequired = (error: unknown): boolean =>
  apiErrorCode(error) === 'STEP_UP_REQUIRED';

/** 412 STALE_VERSION (changed by someone else meanwhile) or 428 (version missing). */
export const isStaleVersion = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.status === 412 || error.status === 428 || error.code === 'STALE_VERSION');

export const isNameTaken = (error: unknown): boolean => apiErrorCode(error) === 'FOLDER_NAME_TAKEN';

/** A plain-language message for an API error (never the raw server text). */
export function folderErrorMessage(t: Translate, error: unknown): string {
  if (!(error instanceof ApiError)) return t('common:errors.generic');
  if (isNameTaken(error)) return t('errors.nameTaken');
  if (isStaleVersion(error)) return t('errors.stale');
  if (error.code === 'VALIDATION_FAILED') {
    const detail = error.fieldErrors[0]?.code;
    if (detail === 'FOLDER_CYCLE') return t('errors.cycle');
    if (detail === 'FOLDER_TOO_DEEP') return t('errors.tooDeep');
    if (detail === 'ACL_SUBJECT_NOT_ALLOWED') return t('errors.subjectNotAllowed');
    return t('errors.invalid');
  }
  if (error.code === 'LEGAL_HOLD') return t('errors.legalHold');
  if (error.code === 'FORBIDDEN') return t('errors.forbidden');
  if (error.code === 'NOT_FOUND') return t('errors.notFound');
  return t(`common:errors.${error.code}`, { defaultValue: t('common:errors.generic') });
}
